import React from 'react';
import { motion } from 'framer-motion';
import { Layers, Mic2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { VideoScriptsPanel } from '@/components/outline/VideoScriptsPanel';
import type { VideoOutline } from '@/stores/videoWorkspaceStore';
import { SceneAudioPanel } from './SceneAudioPanel';
import { BackgroundMusicSection } from './BackgroundMusicSection';
import { PanelHeaderB } from './PanelHeaderB';
import { studioBOn } from '@/lib/studioB/flag';
import { SIDE_WIDTH } from '@/lib/studioB/useRoomForBothSides';
import { bMove } from '@/lib/studioB/motion';

type SceneAudioProps = React.ComponentProps<typeof SceneAudioPanel>;
type MusicProps = React.ComponentProps<typeof BackgroundMusicSection>;

interface StudioAudioPanelProps {
  visible: boolean;
  open: boolean;
  isOutlineTab: boolean;
  scriptsOutline: VideoOutline | null;
  conversationId: string;
  sceneConversationId: SceneAudioProps['sceneConversationId'];
  currentSlideId: SceneAudioProps['currentSlideId'];
  currentSlideData: SceneAudioProps['currentSlideData'];
  mode: SceneAudioProps['mode'];
  onClose: () => void;
  onMusicChange: MusicProps['onMusicChange'];
  onNarratorVolumeChange: MusicProps['onNarratorVolumeChange'];
  /** Direction B on a phone: a full-width sheet under the bar instead of a side column. */
  sheet?: boolean;
}

const SHEET = 'fixed inset-x-0 bottom-0 top-[calc(var(--b-topbar)+env(safe-area-inset-top))] z-40 flex flex-col overflow-hidden rounded-t-[var(--b-radius-sheet)] border-t border-b-line bg-b-panel';

/** The right panel for sound: the scene's audio and the video's music in the studio, the scripts on the Outline tab. */
export const StudioAudioPanel: React.FC<StudioAudioPanelProps> = ({
  visible,
  open,
  isOutlineTab,
  scriptsOutline,
  conversationId,
  sceneConversationId,
  currentSlideId,
  currentSlideData,
  mode,
  onClose,
  onMusicChange,
  onNarratorVolumeChange,
  sheet = false,
}) => {
  const b = studioBOn();
  if (sheet && !visible) return null;
  return (
    <motion.div
      {...(b ? { 'data-b-surface': '' } : {})}
      {...(sheet ? { 'data-sheet-panel': '' } : {})}
      className={cn(
        sheet ? SHEET
        : b
          ? "flex-shrink-0 flex flex-col overflow-hidden"
          : "flex-shrink-0 flex flex-col studio-panel-elevated overflow-hidden"
      )}
      initial={false}
      animate={sheet ? { opacity: 1 } : {
        width: visible ? (b ? SIDE_WIDTH : 340) : 0,
        opacity: visible ? 1 : 0,
      }}
      transition={b ? bMove() : { type: "spring", stiffness: 300, damping: 30 }}
    >
      <div
        {...(b && !sheet ? { 'data-floating-panel': '' } : {})}
        className={cn(
          sheet ? "w-full h-full flex flex-col" : b ? "w-[380px] h-full flex flex-col" : "w-[340px] h-full flex flex-col",
          !sheet && (b ? "transition-transform duration-b-move ease-b motion-reduce:transition-none" : "transition-transform duration-300 ease-out"),
          !sheet && (open ? "translate-x-0" : "translate-x-full")
        )}
      >
        {/* Panel Header - Different for Outline vs Studio */}
        {b ? <PanelHeaderB title={isOutlineTab ? 'Scripts' : 'Audio & script'} onClose={onClose} /> : (
        <div className="h-10 flex items-center justify-between px-3 flex-shrink-0">
          <div className="flex items-center gap-2">
            {isOutlineTab ? (
              <>
                <div className="w-6 h-6 rounded-lg bg-accent flex items-center justify-center">
                  <Layers className="w-3.5 h-3.5 text-accent-foreground" />
                </div>
                <span className="text-xs font-semibold studio-text-primary">Video Scripts</span>
              </>
            ) : (
              <>
                <div className="w-6 h-6 rounded-lg bg-accent flex items-center justify-center">
                  <Mic2 className="w-3.5 h-3.5 text-accent-foreground" />
                </div>
                <span className="text-xs font-semibold studio-text-primary">Audio & Script</span>
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-md flex items-center justify-center studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        )}
        {/* Panel Content - Different for Outline vs Studio */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {scriptsOutline ? (
            <VideoScriptsPanel
              conversationId={conversationId}
              outline={scriptsOutline}
              className="h-full"
            />
          ) : (
            <>
              {/* Scrollable content area for audio panel + background music */}
              <div className="flex-1 min-h-0 overflow-y-auto">
                {/* Current Slide Audio Panel */}
                <div className="min-h-0">
                  <SceneAudioPanel
                    conversationId={conversationId}
                    sceneConversationId={sceneConversationId}
                    currentSlideId={currentSlideId}
                    currentSlideData={currentSlideData}
                    mode={mode}
                  />
                </div>

                {/* Background Music Section - Video-level music */}
                <BackgroundMusicSection
                  conversationId={conversationId}
                  onMusicChange={onMusicChange}
                  onNarratorVolumeChange={onNarratorVolumeChange}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};
